import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { X, User, Lock, Mail, Shield, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import userService, { User as UserType, CreateUserRequest } from '../services/userService';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/dateUtils';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: UserType | null;
  mode: 'create' | 'edit';
}

interface FormData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  role: 'A' | 'S' | 'O' | 'M' | 'W' | 'R';
  isActive: boolean;
}

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, user, mode }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    role: 'O', // Default to Operator
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && user) {
        setFormData({
          username: user.username,
          email: user.email || '',
          firstName: user.firstName || user.fullName?.split(' ')[0] || '',
          lastName: user.lastName || user.fullName?.split(' ').slice(1).join(' ') || '',
          password: '',
          confirmPassword: '',
          role: user.role as 'A' | 'S' | 'O' | 'M' | 'W' | 'R',
          isActive: user.isActive ?? !user.disabled,
        });
      } else {
        setFormData({
          username: '',
          email: '',
          firstName: '',
          lastName: '',
          password: '',
          confirmPassword: '',
          role: 'O', // Default to Operator
          isActive: true,
        });
      }
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen, mode, user]);

  // Create user mutation
  const createMutation = useMutation(
    (data: CreateUserRequest) => userService.createUser(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        toast.success('User created successfully');
        onClose();
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to create user';
        if (error?.response?.status === 409) {
          errorMessage = 'Username already exists. Please choose a different username.';
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
      },
    }
  );

  // Update user mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<CreateUserRequest> }) =>
      userService.updateUser(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        toast.success('User updated successfully');
        onClose();
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to update user';
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
      },
    }
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Password validation (required for create, optional for edit)
    if (mode === 'create' || formData.password) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const requestData: CreateUserRequest = {
      username: formData.username.trim(),
      email: formData.email.trim().toLowerCase(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      role: formData.role,
      isActive: formData.isActive,
    };

    // Only include password if provided
    if (formData.password) {
      requestData.password = formData.password;
    }


    if (mode === 'create') {
      createMutation.mutate(requestData);
    } else if (mode === 'edit' && user) {
      updateMutation.mutate({ id: user.id, data: requestData });
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'A': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'S': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'M': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'O': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'W': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'R': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-secondary-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
                  {mode === 'create' ? 'Create New User' : 'Edit User'}
                </h3>
                <p className="text-sm text-secondary-500 dark:text-gray-400">
                  {mode === 'create' 
                    ? 'Add a new user to the production management system'
                    : 'Update user information and permissions'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600"
              disabled={isLoading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h4 className="flex items-center text-sm font-medium text-secondary-900 dark:text-white">
                <User className="w-4 h-4 mr-2" />
                Personal Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`input-field ${errors.firstName ? 'border-red-300' : ''}`}
                    placeholder="Enter first name"
                    disabled={isLoading}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`input-field ${errors.lastName ? 'border-red-300' : ''}`}
                    placeholder="Enter last name"
                    disabled={isLoading}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="space-y-4">
              <h4 className="flex items-center text-sm font-medium text-secondary-900 dark:text-white">
                <Mail className="w-4 h-4 mr-2" />
                Account Information
              </h4>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className={`input-field ${errors.username ? 'border-red-300' : ''}`}
                  placeholder="Enter username"
                  disabled={isLoading}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors.username}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`input-field ${errors.email ? 'border-red-300' : ''}`}
                  placeholder="Enter email address"
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Password Section */}
            <div className="space-y-4">
              <h4 className="flex items-center text-sm font-medium text-secondary-900 dark:text-white">
                <Lock className="w-4 h-4 mr-2" />
                {mode === 'edit' ? 'Change Password (Optional)' : 'Password *'}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    {mode === 'create' ? 'Password *' : 'New Password'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`input-field pr-10 ${errors.password ? 'border-red-300' : ''}`}
                      placeholder={mode === 'create' ? 'Enter password' : 'Leave blank to keep current'}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 text-secondary-400" /> : <Eye className="w-4 h-4 text-secondary-400" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                      placeholder="Confirm password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4 text-secondary-400" /> : <Eye className="w-4 h-4 text-secondary-400" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="space-y-4">
              <h4 className="flex items-center text-sm font-medium text-secondary-900 dark:text-white">
                <Shield className="w-4 h-4 mr-2" />
                Permissions & Status
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    User Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value as any)}
                    className="input-field"
                    disabled={isLoading}
                  >
                    <option value="O">O - Operator</option>
                    <option value="S">S - Supervisor</option>
                    <option value="M">M - Manager</option>
                    <option value="W">W - Warehouse</option>
                    <option value="R">R - Route</option>
                    <option value="A">A - Admin</option>
                  </select>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(formData.role)}`}>
                      {formData.role === 'A' ? 'Admin' : 
                       formData.role === 'S' ? 'Supervisor' :
                       formData.role === 'M' ? 'Manager' :
                       formData.role === 'O' ? 'Operator' :
                       formData.role === 'W' ? 'Warehouse' :
                       formData.role === 'R' ? 'Route' : formData.role}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-2">
                    Account Status
                  </label>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                      disabled={isLoading}
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-secondary-700">
                      Account is active and can log in
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Current User Info (Edit Mode Only) */}
            {mode === 'edit' && user && (
              <div className="bg-secondary-50 dark:bg-gray-700 border border-secondary-200 dark:border-gray-600 rounded-md p-4">
                <h4 className="flex items-center text-sm font-medium text-secondary-900 dark:text-white mb-2">
                  <User className="w-4 h-4 mr-2" />
                  Current User Status
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-secondary-500">Last Login:</span>
                    <span className="ml-2 font-medium">
                      {(user.lastLogin || user.last_login) ? formatDate(user.lastLogin || user.last_login) : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-500">Created:</span>
                    <span className="ml-2 font-medium">
                      {(user.createdAt || user.created_at) ? formatDate(user.createdAt || user.created_at) : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-secondary-300 dark:border-gray-600 rounded-md hover:bg-secondary-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={isLoading}
              >
                {isLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                )}
                {mode === 'create' ? 'Create User' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserModal;